import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { fetchSummary } from "./fusionApi";

/**
 * Loads GET /fusion/summary. Shared by the Dashboard (heatmap clickability) and the Fusion panel,
 * so both read the same figures. `reload({ refresh: true })` is what a Fusion action calls
 * afterwards: the server rebuilds this user's workspace unless it was built in the last few
 * seconds (and, with a distributed cache, moves everyone else off the old cached responses).
 */
export const useFusionSummary = (enabled = true) => {
  const apiCall = useApi();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // null | "forbidden" | "failed"

  const reload = useCallback(async ({ refresh = false } = {}) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setSummary((await fetchSummary(apiCall, { refresh })) || null);
    } catch (err) {
      setError(err?.response?.status === 403 ? "forbidden" : "failed");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [apiCall, enabled]);

  useEffect(() => { reload(); }, [reload]);

  return { summary, loading, error, reload };
};

/** Chains for one heatmap cell, or 0 — a cell is clickable only when this is above zero. */
export const fusionCellCount = (summary, bucket, severity) =>
  summary?.heatmap?.find((c) => c.bucket === bucket && c.severity === severity)?.chains || 0;
